import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Info } from 'lucide-react';
import { useAuth } from './AuthContext';
import { API_URL } from '../api/config';
const RAZORPAY_KEY_ID = 'rzp_test_gipoJjrJig1ZGh';

const SubscriptionPage = () => {
  const { userToken: token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch subscription status');
      }
      
      const data = await response.json();
      setCurrentSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      alert('Error: Failed to fetch subscription status');
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const handleSubscription = async (plan) => {
    try {
      setProcessingPayment(true);
      
      const orderResponse = await fetch(`${API_URL}/subscription/create-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan })
      });
      
      if (!orderResponse.ok) {
        throw new Error('Failed to create subscription order');
      }
      
      const orderData = await orderResponse.json();

      const razorpayLoaded = await loadRazorpay();
      
      if (!razorpayLoaded) {
        alert('Razorpay SDK failed to load. Please check your connection');
        return;
      }

      const options = {
        description: `Subscription for ${plan} plan`,
        image: 'https://your-logo-url.com/logo.png', 
        currency: 'INR',
        key: RAZORPAY_KEY_ID, 
        amount: orderData.amount,
        name: 'TOBO',
        order_id: orderData.orderId,
        prefill: {
          email: 'tobo@mail.com', 
          contact: '1234567899', 
          name: 'TOBO' 
        },
        theme: { color: '#4F46E5' },
        handler: async function (response) {
          try {
            const paymentResponse = await fetch(`${API_URL}/subscription/verify-payment`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                razorpayOrderId: orderData.orderId,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
                plan: plan
              })
            });

            if (!paymentResponse.ok) {
              throw new Error('Failed to verify payment');
            }

            const paymentData = await paymentResponse.json();

            if (paymentData.success) {
              alert(`Your ${plan} subscription has been activated successfully.`);
              fetchSubscriptionStatus();
              navigate('/subscription-protected-expense-report');
            }
          } catch (error) {
            console.error('Verification error:', error);
            alert('Payment Verification Error: ' + error.message);
          } finally {
            setProcessingPayment(false);
          }
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Error: ' + (error.message || 'Failed to subscribe'));
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50 items-center justify-center p-5">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 text-center mb-8">Choose Your Subscription Plan</h1>
        
        {currentSubscription && currentSubscription.status === 'active' && (
          <div className="bg-indigo-100 rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-indigo-600 mb-2">Current Plan</h2>
            <p className="text-gray-700">
              You are currently subscribed to the <span className="font-semibold text-indigo-600">{currentSubscription.plan}</span> plan.
            </p>
            <p className="text-gray-700">
              Valid until: <span className="font-semibold text-indigo-600">
                {new Date(currentSubscription.endDate).toLocaleDateString()}
              </span>
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-1 gap-6">
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-indigo-600">Premium</h2>
                <CreditCard className="text-indigo-600" size={24} />
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <div className="text-indigo-600 mr-2">•</div>
                  <p className="text-gray-600">Access to Expense and Sales reports</p>
                </div>
                <div className="flex items-start">
                  <div className="text-indigo-600 mr-2">•</div>
                  <p className="text-gray-600">Weekly and Monthly analytics</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <button 
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors flex items-center justify-center"
                  onClick={() => handleSubscription('1-month')}
                  disabled={processingPayment}
                >
                  {processingPayment ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : null}
                  ₹1 for 1 Month
                </button>
                
                <button 
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                  onClick={() => handleSubscription('6-months')}
                  disabled={processingPayment}
                >
                  ₹5 for 6 Months
                </button>
                
                <button 
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors"
                  onClick={() => handleSubscription('1-year')}
                  disabled={processingPayment}
                >
                  ₹10 for 1 Year
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
          <div className="flex">
            <Info className="text-amber-700 mr-2 flex-shrink-0" size={20} />
            <div>
              <p className="text-amber-800 mb-1">• All prices are inclusive of taxes</p>
              <p className="text-amber-800">• For any issues with payments, please contact support</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;